import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMXN, fmtDate } from "../lib/api";
import Topbar from "../components/Topbar";
import EmptyState from "../components/EmptyState";
import GoodsReceiptModal from "../components/GoodsReceiptModal";
import KpiCard from "../components/KpiCard";
import { Truck, Package, CheckCircle2, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function Receptions() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [confirmed, setConfirmed] = useState([]);
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const [p, c] = await Promise.all([
      api.get("/purchase-orders/pending-receipt/list"),
      api.get("/goods-receipts"),
    ]);
    setPending(p.data);
    setConfirmed(c.data);
  };

  useEffect(() => { load(); }, []);

  const exactCount = confirmed.filter((r) => r.status === "confirmed").length;
  const diffCount = confirmed.filter((r) => r.status === "discrepancy").length;

  return (
    <>
      <Topbar title={t("receptions.title")} subtitle={t("receptions.subtitle")} />
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiCard testId="kpi-rec-pending" label={t("receptions.kpiToReceive")} value={pending.length} icon={Truck} accent={pending.length > 0} />
          <KpiCard testId="kpi-rec-exact" label={t("receptions.kpiExact")} value={exactCount} icon={CheckCircle2} />
          <KpiCard testId="kpi-rec-diff" label={t("receptions.kpiDiffs")} value={diffCount} icon={AlertTriangle} />
        </div>

        <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1 w-fit">
          <button onClick={() => setTab("pending")} data-testid="rec-tab-pending"
            className={`px-4 py-2 text-sm font-semibold rounded-lg ${tab === "pending" ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {t("receptions.tabPending", { count: pending.length })}
          </button>
          <button onClick={() => setTab("confirmed")} data-testid="rec-tab-confirmed"
            className={`px-4 py-2 text-sm font-semibold rounded-lg ${tab === "confirmed" ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {t("receptions.tabConfirmed", { count: confirmed.length })}
          </button>
        </div>

        {tab === "pending" ? (
          pending.length === 0 ? (
            <EmptyState title={t("receptions.noPendingTitle")} message={t("receptions.noPendingMsg")} />
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("receptions.tableFolio")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("receptions.tableSupplier")}</th>
                    <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("receptions.tableTotal")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.date")}</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((po, idx) => (
                    <motion.tr key={po.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-6 py-3.5 font-mono text-sm font-semibold cursor-pointer" onClick={() => navigate(`/ocs/${po.id}`)}>{po.folio}</td>
                      <td className="px-6 py-3.5 text-sm">
                        <div className="font-semibold">{po.supplier_name}</div>
                        <div className="text-xs text-slate-500">{t("receptions.items", { count: po.items?.length || 0 })}</div>
                      </td>
                      <td className="px-6 py-3.5 text-sm font-semibold text-right">{fmtMXN(po.total)}</td>
                      <td className="px-6 py-3.5 text-sm text-slate-500">{t("receptions.approvedDate", { date: fmtDate(po.approved_at) })}</td>
                      <td className="px-6 py-3.5 text-right">
                        <button data-testid={`rec-confirm-btn-${po.folio}`} onClick={() => setOpen(po.id)}
                          className="btn-primary-chizy px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ml-auto">
                          <Package className="w-3 h-3" /> {t("receptions.confirmReception")}
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          confirmed.length === 0 ? (
            <EmptyState title={t("receptions.noConfirmedTitle")} message={t("receptions.noConfirmedMsg")} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {confirmed.map((r, idx) => (
                <motion.div key={r.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                  className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/ocs/${r.po_id}`)}
                  data-testid={`rec-confirmed-card-${r.po_folio}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-sm font-bold">{r.po_folio}</div>
                    {r.status === "discrepancy" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">
                        <AlertTriangle className="w-3 h-3" /> {t("receptions.withDiffs")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                        <CheckCircle2 className="w-3 h-3" /> {t("receptions.exact")}
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-semibold text-[#1A1A1A]">{r.supplier_name}</div>
                  <div className="text-xs text-slate-500 mt-1">{fmtDate(r.received_at)}</div>
                </motion.div>
              ))}
            </div>
          )
        )}
      </div>

      {open && (
        <GoodsReceiptModal poId={open} onClose={() => setOpen(null)} onSuccess={() => { setOpen(null); load(); }} />
      )}
    </>
  );
}
